import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { mapFirebaseAuthError } from '@mustauto/utils/firebase-auth-errors';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { AppDownloadBanner } from '@/components/AppDownloadBanner';
import { trackLoginFunnel, trackDemoOpened } from '@/lib/funnel';

function formatPhoneDisplay(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function extractInviteCode(input: string): string {
  const m = input.trim().match(UUID_RE);
  return m ? m[0] : input.trim();
}

export function LoginPage() {
  const {
    user, loading, firebaseUser, sendOTP, confirmOTP, signInWithGoogle, signInWithApple,
    signInDemo, register, joinWorkshop, otpSent, needsRegistration,
  } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [name, setName] = useState('');
  const [workshopName, setWorkshopName] = useState('');
  const [regMode, setRegMode] = useState<'create' | 'join'>('create');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [inviteWorkshop, setInviteWorkshop] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Modo demonstração: o botão só aparece quando o backend tem a flag ligada.
  const [demoEnabled, setDemoEnabled] = useState(false);

  const digits = phone.replace(/\D/g, '');
  const isPhoneValid = digits.length === 11;

  // Instrumentação do funil: dispara cada evento de "view" uma única vez por
  // montagem, para medir onde o lead abandona (ver lib/funnel.ts).
  const firedSteps = useRef<Set<string>>(new Set());
  function fireOnce(step: 'login_entry_view' | 'registration_shown') {
    if (firedSteps.current.has(step)) return;
    firedSteps.current.add(step);
    trackLoginFunnel(step);
  }

  useEffect(() => {
    if (!loading && user && !needsRegistration) {
      navigate({ to: '/dashboard' });
    }
  }, [loading, user, needsRegistration, navigate]);

  // Descobre se o modo demonstração está ligado no backend (rota pública).
  // O botão "Ver demonstração" só aparece quando habilitado — sem isso, o
  // login anônimo cairia em 401 e mostraria o cadastro indevidamente.
  useEffect(() => {
    let cancelled = false;
    api.get('/demo/status')
      .then(({ data }) => { if (!cancelled) setDemoEnabled(!!data.enabled); })
      .catch(() => { /* indisponível: mantém o botão escondido */ });
    return () => { cancelled = true; };
  }, []);

  // Eventos de exibição: tela inicial (entrada) e formulário de cadastro.
  useEffect(() => {
    if (loading) return;
    if (needsRegistration) fireOnce('registration_shown');
    else if (!user && !showPhone && !otpSent) fireOnce('login_entry_view');
  }, [loading, user, needsRegistration, showPhone, otpSent]);

  // Pré-preenche o nome com o do provedor social (Google/Apple), se houver.
  useEffect(() => {
    if (needsRegistration && firebaseUser?.displayName) {
      setName((prev) => (prev ? prev : firebaseUser.displayName ?? ''));
    }
  }, [needsRegistration, firebaseUser]);

  // Valida o código de convite (modo co-gestor) ao digitar.
  useEffect(() => {
    if (regMode !== 'join') return;
    const clean = extractInviteCode(inviteCode);
    setInviteValid(null);
    setInviteWorkshop(null);
    if (clean.length < 36) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/invite/validate/${clean}`);
        if (cancelled) return;
        setInviteValid(!!data.valid);
        setInviteWorkshop(data.workshopName ?? null);
      } catch {
        if (!cancelled) setInviteValid(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [inviteCode, regMode]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  async function handleSocial(kind: 'google' | 'apple') {
    trackLoginFunnel('login_method_selected', { method: kind });
    setSubmitting(true);
    setError('');
    try {
      if (kind === 'google') await signInWithGoogle();
      else await signInWithApple();
    } catch (err) {
      setError(mapFirebaseAuthError(err, 'Não foi possível entrar. Tente novamente.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDemo() {
    trackDemoOpened(); // sinal de interesse (complementa a métrica server-side)
    setSubmitting(true);
    setError('');
    try {
      await signInDemo();
    } catch (err) {
      setError(mapFirebaseAuthError(err, 'Não foi possível abrir a demonstração. Tente novamente.'));
      setSubmitting(false);
    }
    // Em caso de sucesso, onAuthStateChanged navega para o dashboard; manter
    // submitting=true evita flicker do botão durante a transição.
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    if (!isPhoneValid) return;
    setSubmitting(true);
    setError('');
    try {
      await sendOTP(`+55${digits}`);
      trackLoginFunnel('otp_sent');
    } catch (err) {
      setError(mapFirebaseAuthError(err, 'Erro ao enviar o código. Tente novamente.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmCode(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    setError('');
    try {
      await confirmOTP(code);
      trackLoginFunnel('otp_confirmed');
    } catch (err) {
      setError(mapFirebaseAuthError(err, 'Código inválido ou expirado'));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  const canCreate = name.trim().length > 0 && workshopName.trim().length > 0;
  const canJoin = name.trim().length > 0 && inviteValid === true;

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    if (regMode === 'create' ? !canCreate : !canJoin) return;
    trackLoginFunnel('registration_submitted', { mode: regMode });
    setSubmitting(true);
    setError('');
    try {
      if (regMode === 'create') {
        await register({ name: name.trim(), workshopName: workshopName.trim() });
      } else {
        await joinWorkshop({ name: name.trim(), code: extractInviteCode(inviteCode) });
      }
      trackLoginFunnel('registration_completed', { mode: regMode });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-muted">
      <AppDownloadBanner />
      <div className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-xl">
        {/* Logo */}
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-2xl font-bold text-primary-foreground">
            M
          </div>
          <h1 className="text-2xl font-bold text-foreground">MustAuto</h1>
          <p className="mt-1 text-sm text-muted-foreground">Painel do gestor</p>
        </div>

        {needsRegistration ? (
          /* Step 3: Registration — user authenticated but not in DB */
          <form onSubmit={handleRegister}>
            <h2 className="mb-1 text-lg font-bold text-foreground">Quase lá! Vamos criar sua conta</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Só o essencial agora. CNPJ, endereço e horários você completa no próximo passo.
            </p>

            <label className="mb-2 block text-sm font-medium text-foreground">Seu nome</label>
            <input
              type="text"
              className="mb-4 w-full rounded-lg border border-border bg-muted px-4 py-3 text-base text-foreground outline-none focus:border-primary"
              placeholder="Nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />

            {regMode === 'create' ? (
              <>
                <label className="mb-2 block text-sm font-medium text-foreground">Nome da oficina</label>
                <input
                  type="text"
                  className="mb-4 w-full rounded-lg border border-border bg-muted px-4 py-3 text-base text-foreground outline-none focus:border-primary"
                  placeholder="Ex: Auto Center Silva"
                  value={workshopName}
                  onChange={(e) => setWorkshopName(e.target.value)}
                />
              </>
            ) : (
              <>
                <label className="mb-2 block text-sm font-medium text-foreground">Código de convite</label>
                <input
                  type="text"
                  className="mb-2 w-full rounded-lg border border-border bg-muted px-4 py-3 font-mono text-sm text-foreground outline-none focus:border-primary"
                  placeholder="Cole o código ou link de convite"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  autoComplete="off"
                />
                {inviteValid === true && inviteWorkshop && (
                  <p className="mb-4 text-sm font-medium text-success">✓ Oficina: {inviteWorkshop}</p>
                )}
                {inviteValid === false && (
                  <p className="mb-4 text-sm text-destructive">Código inválido ou expirado</p>
                )}
                {inviteValid === null && <div className="mb-4" />}
              </>
            )}

            {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={(regMode === 'create' ? !canCreate : !canJoin) || submitting}
              className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
            >
              {submitting
                ? 'Processando...'
                : regMode === 'create' ? 'Criar conta e configurar →' : 'Entrar na oficina'}
            </button>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              {regMode === 'create' ? (
                <>Já recebeu um convite?{' '}
                  <button type="button" className="font-semibold text-primary" onClick={() => { setRegMode('join'); setError(''); }}>
                    Entrar em uma oficina existente
                  </button>
                </>
              ) : (
                <button type="button" className="font-semibold text-primary" onClick={() => { setRegMode('create'); setError(''); }}>
                  Voltar para criar uma oficina nova
                </button>
              )}
            </p>
          </form>
        ) : showPhone && !otpSent ? (
          /* Phone step 1: number */
          <form onSubmit={handleSendCode}>
            <label className="mb-2 block text-sm font-medium text-foreground">Número de celular</label>
            <div className="mb-4 flex items-center rounded-lg border border-border bg-muted px-4">
              <span className="text-sm font-semibold text-foreground">+55</span>
              <input
                type="tel"
                className="flex-1 bg-transparent py-3 pl-2 text-base text-foreground outline-none placeholder:text-muted-foreground"
                placeholder="(11) 99999-0000"
                value={formatPhoneDisplay(phone)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                autoFocus
              />
            </div>
            {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={!isPhoneValid || submitting}
              className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
            >
              {submitting ? 'Enviando...' : 'Enviar código'}
            </button>
            <button
              type="button"
              className="mt-3 w-full text-center text-sm font-medium text-muted-foreground"
              onClick={() => { setShowPhone(false); setError(''); }}
            >
              ← Outras formas de entrar
            </button>
          </form>
        ) : otpSent ? (
          /* Phone step 2: OTP */
          <form onSubmit={handleConfirmCode}>
            <p className="mb-1 text-sm text-muted-foreground">Código enviado para</p>
            <p className="mb-4 text-sm font-semibold text-foreground">+55 {formatPhoneDisplay(phone)}</p>
            <label className="mb-2 block text-sm font-medium text-foreground">Código de 6 dígitos</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="mb-4 w-full rounded-lg border border-border bg-muted px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-foreground outline-none focus:border-primary"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
            />
            {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={code.length !== 6 || submitting}
              className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
            >
              {submitting ? 'Verificando...' : 'Confirmar'}
            </button>
          </form>
        ) : (
          /* Entry: social + phone */
          <div>
            {/* Proposta de valor — orienta o lead e reduz a barreira do login frio */}
            <div className="mb-4 text-center">
              <h2 className="text-lg font-bold text-foreground">Gestão completa da sua oficina</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Entre ou crie sua conta grátis em segundos.
              </p>
            </div>
            <ul className="mb-5 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="font-bold text-success">✓</span>
                <span>100% grátis — sem mensalidade nem cartão</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success">✓</span>
                <span>OS, estoque, equipe e financeiro num só lugar</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success">✓</span>
                <span>Seu cliente acompanha o serviço pelo app</span>
              </li>
            </ul>
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSocial('google')}
              className="mb-3 flex w-full items-center justify-center gap-2.5 rounded-lg border border-border bg-card py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#4285F4]" /* ux-lint-allow-hex */>G</span>
              Continuar com o Google
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSocial('apple')}
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg bg-black py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
               Continuar com a Apple
            </button>
            <div className="my-3 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> ou <span className="h-px flex-1 bg-border" />
            </div>
            <button
              type="button"
              disabled={submitting}
              onClick={() => { trackLoginFunnel('login_method_selected', { method: 'phone' }); setShowPhone(true); setError(''); }}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              📱 Entrar com telefone
            </button>
            {demoEnabled && (
              <button
                type="button"
                disabled={submitting}
                onClick={handleDemo}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-transparent py-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
              >
                Ver demonstração — explorar sem cadastrar
              </button>
            )}
            {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
            <p className="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
              Ao continuar, você aceita os Termos e a Política de Privacidade.
            </p>
          </div>
        )}

        {/* Invisible reCAPTCHA container */}
        <div id="recaptcha-container" />
      </div>
      </div>
    </div>
  );
}
